import React, { useMemo } from 'react';
// eslint-disable-next-line max-classes-per-file
import { ApolloProvider } from '@apollo/react-hooks';
import { ThemeProvider } from 'styled-components';
import { SessionProvider } from 'next-auth/react';
import { parseCookies } from 'nookies';
import { appWithTranslation, i18n } from 'i18n';
import getConfig from 'next/config';
import Layout from 'components/Layout/Layout';
import Provider from 'components/Provider/Provider';
import GlobalStyle from 'shared/global';
import { Toaster } from 'react-hot-toast';
import CtrlK from 'components/CtrlK/CtrlK';
import { createClient } from 'utils/apollo';
import colors from 'public/colors';
import NProgress from 'nprogress';
import router from 'next/router';
import { BsCheckCircle, BsXCircle } from 'react-icons/bs';
import { CgSpinner } from 'react-icons/cg';

import 'public/static/icomoon/style.css';
import 'public/static/fontstyle.css';
import 'react-nice-dates/build/style.css';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light.css';
import 'tailwindcss/tailwind.css';
import 'styles/globals.css';
import 'styles/tailwind.css';
import 'moment/locale/fr';

const { publicRuntimeConfig } = getConfig();
const { enableCtrlK } = publicRuntimeConfig;

router.onRouteChangeStart = () => {
  NProgress.start();
};

router.onRouteChangeComplete = () => {
  if (window) {
    window.scrollTo(0, 0);
  }

  NProgress.done();
};

router.onRouteChangeError = () => NProgress.done();

function MyApp({ Component, pageProps }) {
  // ⚡ Apollo client initialisé une seule fois
  const apolloClient = useMemo(() => createClient(), []);

  // i18n cookie mode
  const { cimode } = parseCookies();
  if (cimode && i18n.language !== 'cimode') {
    i18n.changeLanguage('cimode');
  }

  // Layout global
  const defaultGetLayout = (page) => <Layout>{page}</Layout>;
  const getLayout = Component.getLayout || defaultGetLayout;

  return (
    <ApolloProvider client={apolloClient}>
      <SessionProvider session={pageProps.session}>
        <Provider theme={{ colors }}>
          <ThemeProvider theme={{ colors }}>
            <GlobalStyle />
            {getLayout(<Component {...pageProps} />)}
            <Toaster
              position="top-right"
              toastOptions={{
                style: { alignItems: 'start' },
                success: {
                  icon: <BsCheckCircle className="my-1.5 ml-1 text-lg text-green-500 lg:text-xl" />,
                },
                error: {
                  icon: <BsXCircle className="my-1.5 ml-1 text-lg text-red-500 lg:text-xl" />,
                },
                loading: {
                  icon: (
                    <CgSpinner className="my-1.5 ml-1 animate-spin text-lg text-gray-700 lg:text-xl" />
                  ),
                },
              }}
            />
            {enableCtrlK && <CtrlK />}
          </ThemeProvider>
        </Provider>
      </SessionProvider>
    </ApolloProvider>
  );
}

export default appWithTranslation(MyApp);
